import { Stack, Typography } from '@mui/material'
import React, { useMemo } from 'react'

import { TARGET_TYPES } from 'components/attacks/create/targets/target-utils'
import { getServiceIdFromAttack } from 'components/attacks/utils/attack-configuration'
import { TruncatedTextWithTooltip } from 'components/common/TruncatedTextWithTooltip'
import {
  getBlastRadiusValue,
  getTargetType
} from 'components/scenarios/concurrent/attack-attributes'
import {
  getServiceHasBeenDeletedMessage,
  useGetServiceAndMarkIsDeleted
} from 'hooks/use-mark-services-as-deleted'

import type { InfraAttackNode } from 'types/api/scenarios/scenario'

export const AttackGraphNodeBlastRadius: React.FC<{
  node: InfraAttackNode
}> = ({ node }) => {
  const serviceId = getServiceIdFromAttack(node)
  const blastRadiusValue = getBlastRadiusValue(node)

  const { isServiceDeleted, service } = useGetServiceAndMarkIsDeleted(serviceId)

  const serviceName = useMemo(() => {
    if (!serviceId) return

    if (isServiceDeleted) {
      return getServiceHasBeenDeletedMessage(serviceId)
    }

    return service?.name ?? serviceId
  }, [isServiceDeleted, service, serviceId])

  const targetType = getTargetType(node)
  const targetTypeDefinition = TARGET_TYPES[targetType]

  const labels = {
    caption: serviceName
      ? `${blastRadiusValue} of selected targets (${targetTypeDefinition.type})`
      : 'of selected targets',
    title: serviceName ?? blastRadiusValue,
    tooltip: serviceName ?? ''
  }

  return (
    <Stack
      sx={{
        alignItems: 'start',
        maxWidth:
          /** icon size plus spacing */
          'calc(100% - 38px)'
      }}
    >
      <TruncatedTextWithTooltip
        sx={{ fontWeight: 'bold', maxWidth: '100%' }}
        tooltipProps={{ title: labels.tooltip }}
      >
        {labels.title}
      </TruncatedTextWithTooltip>
      <Typography variant="secondaryCaption">{labels.caption}</Typography>
    </Stack>
  )
}
